// File: src/components/Footer.jsx
import React from 'react';
import { Box, Container, Grid, Typography, Link, IconButton, Button } from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';
import FacebookIcon from '@mui/icons-material/Facebook';
import TwitterIcon from '@mui/icons-material/Twitter';
import LinkedInIcon from '@mui/icons-material/LinkedIn';
import InstagramIcon from '@mui/icons-material/Instagram';
import PhoneIcon from '@mui/icons-material/Phone';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import LanguageIcon from '@mui/icons-material/Language';
import WhatsAppIcon from '@mui/icons-material/WhatsApp';
import EmailIcon from '@mui/icons-material/Email';
import { keyframes } from '@mui/system';

const pulse = keyframes`
  0% {
    transform: scale(1);
  }
  50% {
    transform: scale(1.05);
  }
  100% {
    transform: scale(1);
  }
`;

const Footer = () => {
  return (
    <Box
      sx={{
        backgroundColor: '#0a233b', // Navy background
        color: '#fbfbfb', // Off-white text
        py: 4,
        mt: 4,
      }}
    >
      <Container maxWidth="lg">
        <Grid container spacing={4}>
          <Grid item xs={12} sm={4}>
            <Typography variant="h6" gutterBottom sx={{ color: '#a6d1ea' }}> {/* Soft blue for headings */}
              About Us
            </Typography>
            <Typography variant="body2" sx={{ mb: 2 }}>
              Lantena is a property management solution delivering innovative services for landlords, tenants, and investors.
            </Typography>
            <Button
              variant="outlined"
              component={RouterLink}
              to="/about-us"
              sx={{
                color: '#a6d1ea',
                borderColor: '#a6d1ea',
                fontSize: '0.8rem',
                '&:hover': {
                  color: '#f66b10',
                  borderColor: '#f66b10',
                  backgroundColor: 'rgba(246, 107, 16, 0.05)'
                },
                animation: `${pulse} 2s infinite ease-in-out`,
              }}
            >
              View More
            </Button>
          </Grid>
          <Grid item xs={12} sm={4}>
            <Typography variant="h6" gutterBottom sx={{ color: '#a6d1ea' }}> {/* Soft blue for headings */}
              Quick Links
            </Typography>
            <Box sx={{ display: 'flex', flexDirection: 'column' }}>
              <Link component={RouterLink} to="/" sx={{ color: 'inherit', mb: 0.5, '&:hover': { color: '#f66b10' } }}> {/* Orange on hover */}
                Home
              </Link>
              <Link component={RouterLink} to="/about-us" sx={{ color: 'inherit', mb: 0.5, '&:hover': { color: '#f66b10' } }}> {/* Orange on hover */}
                About Us
              </Link>
              <Link component={RouterLink} to="/community-blog" sx={{ color: 'inherit', mb: 0.5, '&:hover': { color: '#f66b10' } }}> {/* Orange on hover */}
                Community Blog
              </Link>
              <Link component={RouterLink} to="#" sx={{ color: 'inherit', mb: 0.5, '&:hover': { color: '#f66b10' } }}> {/* Orange on hover */}
                Services
              </Link>
              <Link component={RouterLink} to="/faq" sx={{ color: 'inherit', mb: 0.5, '&:hover': { color: '#f66b10' } }}> {/* Orange on hover */}
                FAQ
              </Link>
              <Link component={RouterLink} to="/contact" sx={{ color: 'inherit', mb: 0.5, '&:hover': { color: '#f66b10' } }}> {/* Orange on hover */}
                Contact
              </Link>
            </Box>
          </Grid>
          <Grid item xs={12} sm={4}>
            <Typography variant="h6" gutterBottom sx={{ color: '#a6d1ea' }}> {/* Soft blue for headings */}
              Contact Us
            </Typography>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <WhatsAppIcon sx={{ color: '#a6d1ea', mr: 1, fontSize: '1rem' }} />
                <Link href="https://wa.me/254722575313" target="_blank" rel="noopener noreferrer" sx={{ color: 'inherit', textDecoration: 'none', '&:hover': { color: '#f66b10' } }}>
                  0722575313
                </Link>
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <PhoneIcon sx={{ color: '#a6d1ea', mr: 1, fontSize: '1rem' }} />
                <Link href="tel:+254736261808" sx={{ color: 'inherit', textDecoration: 'none', '&:hover': { color: '#f66b10' } }}>
                  0736261808
                </Link>
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <LocationOnIcon sx={{ color: '#a6d1ea', mr: 1, fontSize: '1rem' }} />
                <Link href="https://maps.google.com/?q=Ouru+Complex,+Kisii" target="_blank" rel="noopener noreferrer" sx={{ color: 'inherit', textDecoration: 'none', '&:hover': { color: '#f66b10' } }}>
                  Ouru Complex, 3rd floor (Above Equity), Kisii
                </Link>
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <LanguageIcon sx={{ color: '#a6d1ea', mr: 1, fontSize: '1rem' }} />
                <Link href="https://www.lantenapm.com" target="_blank" rel="noopener noreferrer" sx={{ color: 'inherit', textDecoration: 'none', '&:hover': { color: '#f66b10' } }}>
                  www.lantenapm.com
                </Link>
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <EmailIcon sx={{ color: '#a6d1ea', mr: 1, fontSize: '1rem' }} />
                <Link href="mailto:lantenapmanagers@gmail.com" sx={{ color: 'inherit', textDecoration: 'none', '&:hover': { color: '#f66b10' } }}>
                  lantenapmanagers@gmail.com
                </Link>
              </Box>
            </Box>
          </Grid>
        </Grid>

        <Box sx={{ mt: 4, pt: 3, borderTop: '1px solid #6a6565', display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap' }}>
          <Typography variant="body2" sx={{ mb: { xs: 2, md: 0 } }}>
            &copy; {new Date().getFullYear()} Lantena Property Managers. All rights reserved.
          </Typography>

          <Box>
            <IconButton
              href="https://facebook.com"
              target="_blank"
              rel="noopener noreferrer"
              aria-label="Visit our Facebook page"
              sx={{ color: '#a6d1ea', '&:hover': { color: '#f66b10' } }}
            > {/* Soft blue with orange hover */}
              <FacebookIcon fontSize="small" />
            </IconButton>
            <IconButton
              href="https://twitter.com"
              target="_blank"
              rel="noopener noreferrer"
              aria-label="Visit our Twitter page"
              sx={{ color: '#a6d1ea', '&:hover': { color: '#f66b10' } }}
            > {/* Soft blue with orange hover */}
              <TwitterIcon fontSize="small" />
            </IconButton>
            <IconButton
              href="https://linkedin.com"
              target="_blank"
              rel="noopener noreferrer"
              aria-label="Visit our LinkedIn page"
              sx={{ color: '#a6d1ea', '&:hover': { color: '#f66b10' } }}
            > {/* Soft blue with orange hover */}
              <LinkedInIcon fontSize="small" />
            </IconButton>
            <IconButton
              href="https://instagram.com"
              target="_blank"
              rel="noopener noreferrer"
              aria-label="Visit our Instagram page"
              sx={{ color: '#a6d1ea', '&:hover': { color: '#f66b10' } }}
            > {/* Soft blue with orange hover */}
              <InstagramIcon fontSize="small" />
            </IconButton>
          </Box>
        </Box>
      </Container>
    </Box>
  );
};

export default Footer;
